import { DeepReadonly } from 'vue'

import { Field, FieldSpecial } from './Field'

export type QuestionType =
  | 'video'
  | 'openText'
  | 'choice'
  | 'imageChoice'
  | 'nps'
  | 'ranked'
  | 'maxDiffSet'
  | 'gaborGranger'

export type Question<T extends QuestionType = QuestionType> = Readonly<
  {
    openText: QuestionStructure<'openText'>
    choice: QuestionStructure<'choice'>
    imageChoice: QuestionStructure<'imageChoice'>
    video: QuestionStructure<'video'>
    nps: QuestionStructure<'nps'>
    ranked: QuestionStructure<'ranked'>
    maxDiffSet: QuestionStructure<'maxDiffSet'>
    gaborGranger: QuestionStructure<'gaborGranger'>
  }[T]
>

type QuestionStructure<T extends QuestionType> = {
  id: number
  type: T
  title: string
  media?:
    | {
        playbackId: string
        playbackToken: string | undefined
        thumbnailToken: string | undefined
        type: 'mux'
      }
    | {
        url: string
        type: 'video' | 'audio' | 'image'
      }
  fields: DeepReadonly<(QuestionField<T> | FieldSpecial)[]>
} & QuestionExtra<T>

type QuestionField<T extends QuestionType> = {
  openText: Field<'text'>
  choice: Field<'choice' | 'choiceText'>
  imageChoice: Field<'imageChoice'>
  ranked: Field<'ranked'>
  nps: Field<'nps'>
  video: Field<'video'>
  maxDiffSet: Field<'maxDiffSetChoice'>
  gaborGranger: Field<'choice'>
}[T]

type QuestionExtra<T extends QuestionType> = {
  openText: { characterLimit: 60 | 140 | 800 }
  choice: {
    maxAnswers: number
    subject?: string
    subjectImage?: string
  }
  imageChoice: {
    display: 'compact' | 'large'
    maxAnswers: number
    subject?: string
    subjectImage?: string
  }
  video: { prompt: string }
  nps: unknown
  ranked: unknown
  maxDiffSet: {
    bestLabel: string
    worstLabel: string
    subject: string
  }
  gaborGranger: {
    price: Price
  }
}[T]

export type Price = {
  id: string
  value: number
  currency: string
}

export function assertQuestionType<T extends QuestionType>(
  type: unknown,
  expected: T,
): asserts type is T {
  if (type !== expected) throw TypeError(`Expected question type ${expected}, but received ${type}`)
}
